import {
    interstitial,
    AdUnit,
    AD_UNITS,
    Collapse,
} from 'mk/ox/hbAdUnits';
import { PrebidAdUnit } from 'mk/ox/hbTypes';
import { compareForShowOnThisDevice } from 'mk2/containers/AdSlot/AdSlot';
import {
    adSlotRefresh,
    adSlotRenderEnded,
    AdSlotRefreshAction,
    AdSlotRegisterAction,
    AdSlotUnregisterAction,
    AD_SLOT_REFRESH,
    AD_SLOT_REGISTER,
    AD_SLOT_UNREGISTER,
} from 'mk2/containers/AdSlot/AdSlot.actions';
import { PREFIX_AD_SLOT_INTERSTITIAL } from 'mk2/containers/AdSlotInterstitial/AdSlotInterstitial';
import { getLogger } from 'mk2/logger';
import {
    getRequestDeviceMobile,
    getRequestUserHashedEmail,
    getRequestUserHashedId,
    getRequestUserHashedPD,
    AppState,
} from 'mk2/reducers';
import React from 'react';
import { eventChannel } from 'redux-saga';
import { all, call, delay, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';


const PREBID_TIMEOUT = 700;  // how long it takes for bidders to respond with an ad offer
const PREBID_DEBUG = false; // true; see also https://docs.prebid.org/tools/professor-prebid.html
// https://docs.prebid.org/download.html?modules=
//  consentManagementGpp%2C
//  consentManagementTcf%2C
//  gptPreAuction%2C
//  tcfControl%2C
//  adfBidAdapter%2C
//  sizeMappingV2%2C
//  sizeMapping%2C
//  sharedIdSystem%2C
//  criteoIdSystem
const PREBID_JS_SRC = 'https://mkskasmodrykonik.vshcdn.net/js/pb-v3.js';

// mapovanie cien na Line Items v DFP
// http://prebid.org/dev-docs/publisher-api-reference.html#module_pbjs.setPriceGranularity
const PREBID_PRICE_BUCKETS = {
    buckets: [{
        max: 1,
        increment: 0.05,
    }, {
        max: 2,
        increment: 0.1,
    }, {
        max: 5,
        increment: 0.5,
    // }, {
    //     max: 10,
    //     increment: 0.5,
    // }, {
    //     max: 20,
    //     increment: 1.00,
    }],
};

const logger = getLogger('AdSlot.sagas');

let googletag;
let initialized = false;

declare const window;
declare var pbjs: any;

let slotRenderEndedChannel = null;

function slotRenderEndedChannelSubscribe() {
    if (!slotRenderEndedChannel) {
        slotRenderEndedChannel = new Promise((resolve) => {
            googletag.cmd.push(() => {
                const channel = eventChannel((emitter) => {
                    let subscribed = false;

                    const handler = (event: googletag.events.SlotRenderEndedEvent) => {
                        if (!event.slot) {
                            // This handler sometimes catch events from `postMessage`
                            return; // ignore
                        }

                        const slotId = event.slot.getSlotElementId();
                        emitter({ slotId, event: { isEmpty: event.isEmpty, size: event.size } });
                    };

                    googletag.pubads().addEventListener('slotRenderEnded', handler);
                    subscribed = true;
                    return () => {
                        // googletag has no removeEventListener - so we only stop emitting
                        subscribed = false;
                    };
                });

                resolve(channel);
            });
        });
    }

    return slotRenderEndedChannel;
}

async function init(hashedId, hashedEmail, hashedPD) {
    await loadCustomPrebidScript();
    initializeAdSystem(hashedId, hashedEmail, hashedPD);
}

function loadCustomPrebidScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (typeof window.pbjs !== 'undefined') {
            resolve();
            return;
        }

        const existingScript = document.getElementById('prebid-script');
        if (existingScript) {
            // Ak už skript existuje, počkaj na jeho načítanie alebo chybu
            if ((existingScript as HTMLScriptElement).hasAttribute('data-loaded')) {
                // Skript už je načítaný
                resolve();
            } else {
                existingScript.addEventListener('load', () => {
                    (existingScript as HTMLScriptElement).setAttribute('data-loaded', 'true');
                    resolve();
                });
                existingScript.addEventListener('error', () => reject(new Error('Failed to load Prebid.js')));
            }
            return;
        }

        const script = document.createElement('script');
        script.id = 'prebid-script';
        script.src = PREBID_JS_SRC;
        script.async = true;
        script.onload = () => {
            script.setAttribute('data-loaded', 'true');
            resolve();
        };
        script.onerror = (err) => reject(new Error('Failed to load Prebid.js'));
        document.head.appendChild(script);
    });
}

function initializeAdSystem(hashedId, hashedEmail, hashedPD) {
    if (!pbjs) {
        // Prebid.js not loaded
        return;
    }

    if (initialized) {
        // Already initialized - ignore next call
        return;
    }

    initialized = true;

    pbjs.processQueue();

    googletag = window.googletag || {};
    googletag.cmd = googletag.cmd || [];
    window.googletag = googletag;

    googletag.cmd.push(() => {
        const pubads = googletag.pubads();
        pubads.disableInitialLoad();
        pubads.enableSingleRequest();

        // Global collapse setting (can be overridden per slot)
        // Ad slots will stay empty most of the time. So, collapse all
        // divs on the page BEFORE the browser fetches any ads
        // https://support.google.com/dfp_premium/answer/3072674?hl=en
        pubads.collapseEmptyDivs(true);

        if (hashedId) {
            pubads.setTargeting('email_hash', hashedEmail);
        }
        if (hashedEmail) {
            pubads.setTargeting('user_id_hash', hashedId);
        }
        googletag.enableServices();
    });

    slotRenderEndedChannelSubscribe();
    const pd = hashedPD || undefined;

    pbjs.que.push(() => {
        pbjs.setConfig({
            // ...debuggingConf,
            priceGranularity: PREBID_PRICE_BUCKETS,
            bidderTimeout: PREBID_TIMEOUT,
            pubcid: { expInterval: 525600 }, // expiry 1 year (default)
            consentManagement: {
                allowAuctionWithoutConsent: false,
                tcf: {
                    cmpApi: 'iab',
                    timeout: 1000,
                },
                gdpr: {
                    cmpApi: 'iab',
                    defaultGdprScope: true,
                    timeout: 3000,
                    // allowAuctionWithoutConsent: false,  // deprecated
                    rules: [{
                        // don’t enforce Purpose 1 except for bidderA.
                        // this means that TCF approval is only considered
                        // for this one bidder.
                        purpose: 'storage',
                        enforcePurpose: false,
                        enforceVendor: false,
                        vendorExceptions: ['adf'],
                    }, {
                        // Allow the user to object to vendors for Purpose 2
                        // but don’t allow them to turn off header bidding
                        // altogether.
                        // The exception is bidderB, which the user will
                        // not be allowed to reject
                        purpose: 'basicAds',
                        enforcePurpose: false,
                        enforceVendor: true,
                        vendorExceptions: ['adf'],
                    }, {
                        // allow the user to have their userId removed from
                        // header bidding except for bidders A and B
                        purpose: 'personalizedAds',
                        enforcePurpose: true,
                        enforceVendor: true,
                        vendorExceptions: ['adf'],
                    }, {
                        // analytics adapters are ok
                        purpose: 'measurement',
                        enforcePurpose: false,
                        enforceVendor: false,
                    }],
                },
            },
            debug: PREBID_DEBUG,
            userSync: {
                // auctionDelay: 500,   // default
                filterSettings: {
                    iframe: {
                        bidders: '*',
                        filter: 'include',
                    },
                    image: {
                        bidders: '*',
                        filter: 'include',
                    },
                },
                userIds: [
                    {
                        name: 'criteo',
                    },
                    {
                        name: 'czechAdId',
                    },
                    {
                        name: 'sharedId',
                        storage: {
                            // name of the 1st party cookie, _pubcid is supported for backwards compatibility
                            name: '_sharedid',
                            type: 'cookie',
                            expires: 365,
                        },
                    },
                    {
                        name: 'id5Id',
                        params: {
                            partner: 1832,
                            externalModuleUrl: 'https://cdn.id5-sync.com/api/1.0/id5PrebidModule.js',
                            pd,
                        },
                        storage: {
                            type: 'html5',
                            name: 'id5id',
                            expires: 90,
                            refreshInSeconds: 7200,
                        },
                    },
                    // {
                    //     name: 'identityLink',
                    //     params: {
                    //         pid: '999',             // Set your Placement ID here
                    //         notUse3P: false,
                    //     },
                    //     storage: {
                    //         type: 'html5',
                    //         name: 'idl_env',        // "idl_env" is the required storage name
                    //         expires: 15,            // RampID envelope can last for 15 days
                    //         refreshInSeconds: 1800,  // RampID envelope will be updated every 30 minutes
                    //     },
                    // },
                ],
            },
        });
        // TODO: Move enable analytics to mk/settings as a flag
        // if (enableAnalytics) {
        //     this.pbjs.enableAnalytics({
        //         provider: 'ga',
        //         options: {
        //             global: 'ga', // <string> name of GA global. Default is 'ga'
        //             trackerName: 'pbjsTracker',
        //             enableDistribution: true,
        //         },
        //     });
        // }
    });
}

const registeredSlots = new Map<string, googletag.Slot>();
const registering = new Set<string>();

function* registerAdSlot({ slotId, zone, targeting }: AdSlotRegisterAction) {
    // Prevent double registrations
    if (registering.has(slotId) || registeredSlots.has(slotId)) {
        return;
    }

    registering.add(slotId);

    const hashedId = yield select(getRequestUserHashedId);
    const hashedEmail = yield select(getRequestUserHashedEmail);
    const hashedPD = yield select(getRequestUserHashedPD);

    // Allways call init - init is smart to not reinitialize yourself

    yield call(init, hashedId, hashedEmail, hashedPD);

    const adUnit: AdUnit = AD_UNITS[zone];

    const registerPbjs = new Promise<void>((resolve) => {
        pbjs.que = pbjs.que || [];
        pbjs.que.push(() => {
            if (!registering.has(slotId)) {
                return;
            }

            const firstValidSizes = adUnit.sizeConfig.find(sc => sc.bannerSizes && sc.bannerSizes.length)?.bannerSizes || [];

            const pbjsAdUnit: PrebidAdUnit = {
                code: slotId,
                bids: adUnit.bids,
                mediaTypes: {
                    banner: {
                        sizes: firstValidSizes,
                        name: adUnit.name,
                        // sizeConfig: adUnit.sizeConfig.map((sc) => ({
                        //     minViewPort: [sc.minScreenWidth, 0],
                        //     sizes: sc.bannerSizes,
                        // })),
                    },
                },
            };
            pbjs.addAdUnits([pbjsAdUnit]);
            resolve();
        });
    });

    const registerGoogleTag = new Promise((resolve) => {
        googletag.cmd.push(() => {
            if (!registering.has(slotId)) {
                return;
            }

            // https://support.google.com/dfp_premium/answer/3423562?hl=en
            let gptSizeMappingBuilder: googletag.SizeMappingBuilder = googletag.sizeMapping();
            const allSlotSizes = [];
            adUnit.sizeConfig.forEach((sc) => {
                const screenSize = [sc.minScreenWidth, 0];
                const slotSizes: googletag.SingleSize[] = [...sc.bannerSizes];
                if (sc.native) slotSizes.push('fluid');
                allSlotSizes.push(...slotSizes);
                gptSizeMappingBuilder = gptSizeMappingBuilder.addSize(screenSize, slotSizes);
            });
            const gptSizeMapping: googletag.SizeMappingArray = gptSizeMappingBuilder.build();

            const handlingInterstitial = slotId.startsWith(PREFIX_AD_SLOT_INTERSTITIAL);

            let gSlot: googletag.Slot | null;

            // Define a web ad slot
            // const slotElement = document.getElementById(slotId);

            gSlot = (
                !handlingInterstitial
                    ? googletag.defineSlot(adUnit.gptid, allSlotSizes, slotId)
                    : googletag.defineOutOfPageSlot(
                        adUnit.gptid,
                        googletag.enums.OutOfPageFormat.INTERSTITIAL,
                    )
            );

            // Slot returns null if the page or device does not support such kind of ad slot (e.g. interstitials)
            if (gSlot && !handlingInterstitial) {
                gSlot.defineSizeMapping(gptSizeMapping);
            }

            if (gSlot) {
                gSlot.addService(googletag.pubads());
            }

            // Enable optional interstitial triggers - when interstitial ads will be rolled out
            const enableOptionalInterstitialTriggers = false;

            if (gSlot && handlingInterstitial) {
                // Enable optional interstitial triggers and register the slot.
                // @ts-ignore
                gSlot.setConfig({
                    interstitial: {
                        triggers: {
                            navBar: enableOptionalInterstitialTriggers,
                            unhideWindow: enableOptionalInterstitialTriggers,
                        },
                    },
                });
            }

            if (targeting) {
                Object.keys(targeting).forEach((key: string) => {
                    gSlot.setTargeting(key, targeting[key]);
                });
            }

            // Add event listener to enable navigation once the interstitial loads.
            // If this event doesn't fire, try clearing local storage and refreshing
            // the page.
            // googletag.pubads().addEventListener('slotOnload', (event) => {
            //     const slotName = event.slot?.getSlotId()?.getName();
            //     if ('interst' in slotName) {
            //         alert(`slot loaded ${slotName}`);
            //     }
            // });

            // By default, the detected language of the current page is used to render
            // interstitial UI elements. This behavior can be overridden by manually
            // specifying the document language:
            // googletag.pubads().set("document_language", "en");

            // Set collapsing per zone (before fetch/after fetch/never)
            // https://support.google.com/dfp_premium/answer/3072674?hl=en
            if (adUnit.collapse !== undefined) {
                switch (adUnit.collapse) {
                    case Collapse.NEVER:
                        gSlot.setCollapseEmptyDiv(false);
                        break;
                    case Collapse.AFTER_FETCH:
                        gSlot.setCollapseEmptyDiv(true);
                        break;
                    case Collapse.BEFORE_FETCH:
                        gSlot.setCollapseEmptyDiv(true, true);
                        break;
                    default:
                        // pass
                }
            }

            // For proper behavior across all browsers, calling refresh must be
            // preceded by a call to display the ad slot.
            if (gSlot && !handlingInterstitial) {
                googletag.display(slotId);
            }
            resolve(gSlot);
        });
    });

    const [slot] = yield call(() => Promise.all([registerGoogleTag, registerPbjs]));

    if (registering.delete(slotId)) {
        registeredSlots.set(slotId, slot);
        yield put(adSlotRefresh(slotId));
    }
}

function* unregisterAdSlot({ slotId }: AdSlotUnregisterAction) {
    if (!registeredSlots.has(slotId) // Slot is not registered
        && !registering.has(slotId)) { // Slot is not scheduled for register
        return;
    }

    const hashedId = yield select(getRequestUserHashedId);
    const hashedEmail = yield select(getRequestUserHashedEmail);
    const hashedPD = yield select(getRequestUserHashedPD);

    yield call(init, hashedId, hashedEmail, hashedPD);

    const unregisterPbjs = new Promise<void>((resolve) => {
        pbjs.que.push(() => {
            if (registeredSlots.has(slotId)) {
                pbjs.removeAdUnit(slotId);
            }
            resolve();
        });
    });

    const unregisterGoogleTag = new Promise<void>((resolve) => {
        googletag.cmd.push(() => {
            if (registeredSlots.has(slotId)) {
                googletag.destroySlots([registeredSlots.get(slotId)]);
            }
            resolve();
        });
    });

    yield call(() => Promise.all([unregisterPbjs, unregisterGoogleTag]));

    registeredSlots.delete(slotId);
}

const slotsToRefresh = new Set<string>();

function* refreshAdSlot({ slotId }: AdSlotRefreshAction) {
    if (!registeredSlots.has(slotId) // Slot is not registered
        && !registering.has(slotId)) { // Slot is not scheduled for register
        return;
    }

    slotsToRefresh.add(slotId);

    // yield call(() => new Promise((resolve) => requestAnimationFrame(resolve)));
    yield delay(0);

    // GVL ID from https://iabeurope.eu/vendor-list-tcf/
    pbjs.aliasBidder('adf', 'adform_sk', { gvlid: 50 });

    pbjs.que.push(() => {
        // Now we can refresh ads
        const gSlots = [];
        const slotsIds = [];
        slotsToRefresh.forEach((id: string) => {
            if (registeredSlots.has(id)) {
                gSlots.push(registeredSlots.get(id));
                slotsIds.push(id);
            }
        });
        slotsToRefresh.clear();

        // Some slots can be already unregistered
        if (gSlots.length === 0) {
            return;
        }

        // call bidders
        pbjs.requestBids({
            timeout: PREBID_TIMEOUT,
            adUnitCodes: slotsIds,
            bidsBackHandler: () => {
                googletag.cmd.push(() => {
                    // send request to DFP to display the best bid
                    pbjs.setTargetingForGPTAsync(slotsIds);

                    // DFP compares the best bid with offers from ADX (Google's ad exchange)
                    // and displays the priciest ad
                    googletag.pubads().refresh(gSlots);
                });
            },
        });
    });
}

function* emitRenderEnded() {
    const hashedId = yield select(getRequestUserHashedId);
    const hashedEmail = yield select(getRequestUserHashedEmail);
    const hashedPD = yield select(getRequestUserHashedPD);

    yield call(init, hashedId, hashedEmail, hashedPD);

    const channel = yield call(slotRenderEndedChannelSubscribe);
    try {
        while (true) {
            const { slotId, event } = yield take(channel);
            yield put(adSlotRenderEnded(slotId, event));
        }
    } catch (err) {
        logger.error(err);
    }
}

function* registerAdSlotsFromServer() {
    const slots = yield select((state: AppState) => state.response.registeredSlots);
    const isMobile = yield select(getRequestDeviceMobile);

    for (const key of Object.keys(slots)) {
        const slot = slots[key];
        if (compareForShowOnThisDevice(isMobile, slot.zone)) {
            yield registerAdSlot(slot);
        }
    }
}

export default function* root() {
    if (!process.env.SERVER && window.location?.search?.includes?.('__disable_ads=true')) {
        return null;
    }

    yield all([
        takeEvery(AD_SLOT_REGISTER, registerAdSlot),
        takeEvery(AD_SLOT_UNREGISTER, unregisterAdSlot),
        takeLatest(AD_SLOT_REFRESH, refreshAdSlot),

        process.env.SERVER ? null : fork(emitRenderEnded),
        process.env.SERVER ? null : fork(registerAdSlotsFromServer),
    ]);
}
